interface CustomerData {
  smart_key: string;
  username: string;
  currency: string;
  avatar_url?: string;
  balance?: number;
  last_updated: number;
}

interface VendorCache {
  vendor_smart_key: string;
  customers: CustomerData[];
  last_sync: number;
}

class LocalCustomerDB {
  private dbName = 'AjelCustomerCache';
  private version = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create customers store
        if (!db.objectStoreNames.contains('customers')) {
          const store = db.createObjectStore('customers', { keyPath: 'vendor_smart_key' });
          store.createIndex('vendor_smart_key', 'vendor_smart_key', { unique: true });
        }
      };
    });
  }

  async cacheCustomers(vendorSmartKey: string, customers: CustomerData[]): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customers'], 'readwrite');
      const store = transaction.objectStore('customers');

      const cacheData: VendorCache = {
        vendor_smart_key: vendorSmartKey,
        customers: customers.map(customer => ({
          ...customer,
          last_updated: Date.now()
        })),
        last_sync: Date.now()
      };

      const request = store.put(cacheData);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedCustomers(vendorSmartKey: string): Promise<CustomerData[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customers'], 'readonly');
      const store = transaction.objectStore('customers');
      const request = store.get(vendorSmartKey);

      request.onsuccess = () => {
        const result = request.result as VendorCache;
        if (result) {
          // Check if cache is still valid (24 hours)
          const isStale = Date.now() - result.last_sync > 24 * 60 * 60 * 1000;
          if (!isStale) {
            resolve(result.customers);
            return;
          }
        }
        resolve([]);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async searchCachedCustomers(vendorSmartKey: string, query: string): Promise<CustomerData[]> {
    const cachedCustomers = await this.getCachedCustomers(vendorSmartKey);
    
    if (cachedCustomers.length === 0) return [];

    const lowerQuery = query.toLowerCase();
    return cachedCustomers.filter(customer => 
      customer.username.toLowerCase().includes(lowerQuery) ||
      customer.smart_key.toLowerCase().includes(lowerQuery)
    );
  }

  async addCustomerToCache(vendorSmartKey: string, customer: CustomerData): Promise<void> {
    const cachedCustomers = await this.getCachedCustomers(vendorSmartKey);
    
    // Check if customer already exists
    const existingIndex = cachedCustomers.findIndex(c => c.smart_key === customer.smart_key);
    
    if (existingIndex >= 0) {
      // Update existing customer
      cachedCustomers[existingIndex] = {
        ...customer,
        last_updated: Date.now()
      };
    } else {
      // Add new customer
      cachedCustomers.push({
        ...customer,
        last_updated: Date.now()
      });
    }

    await this.cacheCustomers(vendorSmartKey, cachedCustomers);
  }

  async removeCustomerFromCache(vendorSmartKey: string, customerSmartKey: string): Promise<void> {
    const cachedCustomers = await this.getCachedCustomers(vendorSmartKey);
    const filteredCustomers = cachedCustomers.filter(c => c.smart_key !== customerSmartKey);
    await this.cacheCustomers(vendorSmartKey, filteredCustomers);
  }

  async clearCache(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customers'], 'readwrite');
      const store = transaction.objectStore('customers');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async isCacheStale(vendorSmartKey: string): Promise<boolean> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customers'], 'readonly');
      const store = transaction.objectStore('customers');
      const request = store.get(vendorSmartKey);

      request.onsuccess = () => {
        const result = request.result as VendorCache;
        if (!result) {
          resolve(true); // No cache means it's stale
          return;
        }
        
        // Consider cache stale after 1 hour for quick access
        const isStale = Date.now() - result.last_sync > 60 * 60 * 1000;
        resolve(isStale);
      };
      
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export const localCustomerDB = new LocalCustomerDB();